import PropTypes from 'prop-types';
import { useEffect, memo, useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import {
  getCustomerInfoByCustomerId,
  getCustomerInfoStatus,
  searchCustomersRequest,
  setSelectedCustomer,
} from '../../store/slices/customer';
import TwAutocomplete from '../TwAutocomplete';
import useAutocompleteStyles from './styles';
import SingleSelectAutocompleteFilter from '../Filters/SingleSelectAutocompleteFilter';
import Button from '../Button';
import { SearchStatus } from '../../constants';

const CustomerLookup = ({
  doShowInHeaderFilter,
  onChange,
  stateroomIndex,
  passengerIndex,
  setDoOpenWhenPrefilledData,
}) => {
  const dispatch = useDispatch();
  const classes = useAutocompleteStyles();
  const { track } = useAnalytics();
  const { isLoggedIn } = useSelector((store) => store.user);
  const {
    customers = [],
    searchCustomersStatus,
    customerByCustomerId = {},
  } = useSelector((store) => store.customer || {});

  const passengers = useSelector((store) => store.trip?.passengers || {});

  const customerId = useSelector((store) => {
    if (passengerIndex >= 0) {
      const result =
        store.trip.passengers &&
        store.trip.passengers[passengerIndex] &&
        store.trip.passengers[passengerIndex].customerId;
      return result;
    }

    const searchObj = store.tripSearch && store.tripSearch.searchObj;
    const staterooms = searchObj && searchObj.staterooms;

    const result =
      staterooms &&
      Array.isArray(staterooms) &&
      typeof stateroomIndex === 'number' &&
      stateroomIndex >= 0 &&
      staterooms[stateroomIndex] &&
      staterooms[stateroomIndex].customerId;
    return result;
  });

  const customerInfo = useSelector(getCustomerInfoByCustomerId(customerId));
  const customerInfoStatus = useSelector(getCustomerInfoStatus(customerId));

  const [options, setOptions] = useState([{}]);

  const handleChange = useCallback(
    (value) => {
      if (value && value.customerId) {
        dispatch(
          setSelectedCustomer({
            customerByCustomerId: {
              ...customerByCustomerId,
              [value.customerId]: value,
            },
          }),
        );
      } else if (customerId && customerByCustomerId && customerByCustomerId[customerId]) {
        // Check Redux state directly whether any other passenger uses this
        // customerId a case when user clears the CustomerLookup field after
        // mistakenly selecting a customer twice. clear from cache if no
        // other passenger is using it.
        const isCustomerUsedByOtherPassenger = Object.entries(passengers).some(
          ([idx, passenger]) => {
            const indexNum = parseInt(idx, 10);
            const isUsed = indexNum !== passengerIndex && passenger.customerId === customerId;
            return isUsed;
          },
        );

        if (!isCustomerUsedByOtherPassenger) {
          dispatch(
            setSelectedCustomer({
              customerByCustomerId: {
                ...customerByCustomerId,
                [customerId]: undefined,
              },
            }),
          );
        }
      }
      if (onChange && typeof onChange === 'function') {
        onChange(value);
      }
      track('filterBar.handleChangeCustomer', { selectedCustomer: JSON.stringify(value) });
    },
    [dispatch, onChange, track, customerByCustomerId, customerId, passengerIndex, passengers],
  );

  const onInputChange = useCallback(
    (query) => {
      dispatch(
        searchCustomersRequest({
          query,
        }),
      );
    },
    [dispatch],
  );

  const getCustomerDisplayLabel = useCallback((customer) => {
    if (!customer) return '';
    if (!customer.firstName) return '';

    const name = `${customer.firstName} ${customer.lastName}`.trim();
    return customer.email ? `${name} (${customer.email})` : `${name} (no email)`;
  }, []);

  useEffect(() => {
    let filteredCustomers =
      passengerIndex === 0 ? (customers || []).filter((c) => c.email) : customers;

    filteredCustomers = (filteredCustomers || []).map((customer) => ({
      ...customer,
      label: getCustomerDisplayLabel(customer),
    }));

    setOptions([{ suggests: filteredCustomers }]);
  }, [customers, passengerIndex, getCustomerDisplayLabel]);

  useEffect(() => {
    if (isLoggedIn && (!customers || !customers.length)) {
      dispatch(searchCustomersRequest({ pageSize: 50 }));
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (customerInfo && onChange) {
      if (
        typeof setDoOpenWhenPrefilledData === 'function' &&
        passengerIndex !== undefined &&
        customerInfo.documents &&
        customerInfo.documents.length > 0
      ) {
        setDoOpenWhenPrefilledData(passengerIndex, true);
      }
      onChange(customerInfo);
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [customerInfo]);

  if (!isLoggedIn) return <></>;
  return doShowInHeaderFilter ? (
    <SingleSelectAutocompleteFilter
      onChange={handleChange}
      options={options}
      onInputChange={onInputChange}
      tooltipPrependText={customerId && customerInfo ? 'Booking trip for' : ''}
      value={customerInfo}
      label="Select customer to book trip for..."
      filterButtonMaxWidth="max-w-filter"
      widthInside="max-w-85"
      isOptionsLoading={
        customerInfoStatus === SearchStatus.SEARCHING ||
        searchCustomersStatus === SearchStatus.SEARCHING
      }
    />
  ) : (
    <div className="flex">
      <div className="flex-grow">
        <TwAutocomplete
          isMultiple={false}
          placeholderText="Search Travelers..."
          onChange={handleChange}
          options={options}
          onInputChange={onInputChange}
          classes={classes}
          isNested
          disableClearable
          autoFocus={false}
          values={customerInfo}
          freeSolo={false}
          isLoading={searchCustomersStatus === SearchStatus.SEARCHING}
        />
      </div>
      {customerInfo && (
        <Button
          size={Button.buttonSizes.SM}
          variant={Button.buttonVariants.LINK}
          onClick={() => handleChange(undefined)}
        >
          Reset
        </Button>
      )}
    </div>
  );
};

CustomerLookup.propTypes = {
  doShowInHeaderFilter: PropTypes.bool,
  onChange: PropTypes.func,
  stateroomIndex: PropTypes.number,
  passengerIndex: PropTypes.number,
  setDoOpenWhenPrefilledData: PropTypes.func,
};

CustomerLookup.defaultProps = {
  doShowInHeaderFilter: false,
  onChange: undefined,
  stateroomIndex: 0,
  passengerIndex: undefined,
  setDoOpenWhenPrefilledData: undefined,
};

export default memo(CustomerLookup);
